import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { Code } from '@atlaskit/code';
import Lozenge, { ThemeAppearance } from '@atlaskit/lozenge';
import { Box, Flex, Text } from '@atlaskit/primitives';

import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';

import { messages } from '../merge-queues.i18n';
import { useFocusedMergeQueue } from '../provider';
import { MergeQueueState, MergeQueueStrategy } from '../types';

const stateLozengeDetails: {
  [k in MergeQueueState]: {
    label: MessageDescriptor;
    appearance: ThemeAppearance;
  };
} = {
  [MergeQueueState.ACTIVE]: {
    label: messages.active,
    appearance: 'success',
  },
  [MergeQueueState.DRAINING]: {
    label: messages.draining,
    appearance: 'moved',
  },
  [MergeQueueState.INACTIVE]: {
    label: messages.inactive,
    appearance: 'removed',
  },
};

const strategyMessages: {
  [k in MergeQueueStrategy]: MessageDescriptor;
} = {
  [MergeQueueStrategy.OPTIMISTIC_BATCHED_PR]:
    messages.queueStrategyOptimisticBatchedPR,
  [MergeQueueStrategy.OPTIMISTIC_PER_PR]: messages.queueStrategyOptimisticPerPR,
  [MergeQueueStrategy.STRICT_BATCHED_PR]: messages.queueStrategyStrictBatchedPR,
  [MergeQueueStrategy.STRICT_PER_PR]: messages.queueStrategyStrictPerPR,
};

export const MergeQueueDetails = () => {
  const [{ focusedMergeQueue }] = useFocusedMergeQueue();
  const { formatMessage } = useIntl();
  const isMergeQueuesStrategyEnabled = useStatsigGate(
    StatsigFeatureKeys.isMergeQueuesStrategyEnabled
  );
  const focusedMergeQueueState = focusedMergeQueue?.state
    ? stateLozengeDetails[focusedMergeQueue.state]
    : null;

  if (!focusedMergeQueue || !focusedMergeQueueState) {
    return null;
  }

  return (
    <Box paddingBlock="space.300">
      <Flex gap="space.300" alignItems="center">
        <Box>
          <Text>{formatMessage(messages.statusTableHeader)}: </Text>
          <Lozenge appearance={focusedMergeQueueState.appearance}>
            {formatMessage(focusedMergeQueueState.label)}
          </Lozenge>
        </Box>
        <Box>
          <Code>
            <Text>{formatMessage(messages.targetBranchTableHeader)}: </Text>
            <Text>{focusedMergeQueue.branch.name}</Text>
          </Code>
        </Box>
        <Box>
          {isMergeQueuesStrategyEnabled && (
            <Code>
              <Text>{formatMessage(messages.strategyTableHeader)}: </Text>
              <Text>
                {formatMessage(strategyMessages[focusedMergeQueue.strategy])}
              </Text>
            </Code>
          )}
        </Box>
      </Flex>
    </Box>
  );
};
